import { EnvironmentOutlined, GlobalOutlined } from '@ant-design/icons';
import { Button, Input, InputNumber, Segmented, Typography, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BuildingLocation, MapProvider } from '../types';

const { Text } = Typography;

interface BuildingLocationPickerProps {
  value: BuildingLocation | null;
  onChange: (value: BuildingLocation | null) => void;
  enabled?: boolean;
}

const defaultProvider = (): MapProvider => {
  const language = navigator.language.toLowerCase();
  return language.includes('zh') ? 'amap' : 'google';
};

const buildMapUrl = (location: BuildingLocation, provider: MapProvider) => {
  if (provider === 'amap') {
    return `https://uri.amap.com/marker?position=${location.longitude},${location.latitude}&name=${encodeURIComponent(location.address || '建筑位置')}`;
  }

  return `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`;
};

const BuildingLocationPicker: React.FC<BuildingLocationPickerProps> = ({ value, onChange, enabled = true }) => {
  const [locating, setLocating] = useState(false);
  const provider = value?.provider ?? defaultProvider();
  const mapUrl = useMemo(() => (value ? buildMapUrl(value, provider) : ''), [provider, value]);

  const updateLocation = useCallback((patch: Partial<BuildingLocation>) => {
    const next = {
      latitude: value?.latitude ?? 0,
      longitude: value?.longitude ?? 0,
      provider,
      ...value,
      ...patch,
    };

    if (Number.isFinite(next.latitude) && Number.isFinite(next.longitude)) {
      onChange(next);
    }
  }, [onChange, provider, value]);

  const locateCurrent = useCallback(() => {
    if (!enabled) return;
    if (!navigator.geolocation) {
      message.warning('当前浏览器不支持定位，请手动输入经纬度');
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        onChange({
          latitude: Number(position.coords.latitude.toFixed(6)),
          longitude: Number(position.coords.longitude.toFixed(6)),
          address: value?.address || '当前位置',
          provider,
          coordinateSystem: 'wgs84',
        });
        setLocating(false);
      },
      () => {
        message.warning('无法获取当前位置，请手动输入建筑经纬度');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, [enabled, onChange, provider, value?.address]);

  useEffect(() => {
    if (enabled && !value) {
      locateCurrent();
    }
  }, [enabled, locateCurrent, value]);

  return (
    <div className="building-location-panel">
      <div className="building-location-head">
        <span>户型建筑位置</span>
        <Segmented
          size="small"
          value={provider}
          options={[
            { label: '高德', value: 'amap' },
            { label: 'Google', value: 'google' },
          ]}
          onChange={(next) => updateLocation({ provider: next as MapProvider })}
          disabled={!enabled}
        />
      </div>
      <Input
        value={value?.address}
        placeholder="地址备注，默认当前位置"
        prefix={<EnvironmentOutlined />}
        onChange={(event) => updateLocation({ address: event.target.value })}
        disabled={!enabled}
      />
      <div className="building-location-grid">
        <InputNumber
          value={value?.latitude}
          precision={6}
          placeholder="纬度"
          onChange={(next) => updateLocation({ latitude: typeof next === 'number' ? next : 0 })}
          disabled={!enabled}
        />
        <InputNumber
          value={value?.longitude}
          precision={6}
          placeholder="经度"
          onChange={(next) => updateLocation({ longitude: typeof next === 'number' ? next : 0 })}
          disabled={!enabled}
        />
      </div>
      <div className="building-location-actions">
        <Button icon={<EnvironmentOutlined />} loading={locating} disabled={!enabled} onClick={locateCurrent}>
          {enabled ? '当前定位' : '环境分析维护中'}
        </Button>
        <Button
          icon={<GlobalOutlined />}
          disabled={!enabled || !value}
          href={mapUrl}
          target="_blank"
        >
          地图查看
        </Button>
        {value ? (
          <Button type="link" onClick={() => onChange(null)}>
            清除
          </Button>
        ) : null}
      </div>
      <Text type="secondary" className="building-location-note">
        {enabled ? '生成报告时会调用 API 查询 500 米内建筑、道路与河流水体，用于形峦分析。' : '管理员已暂停建筑外局分析，位置功能暂不可用。'}
      </Text>
    </div>
  );
};

export default BuildingLocationPicker;
